import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ArrowDownRight, ArrowUpRight, ChevronLeft, Loader2, RotateCcw, Scale, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const eur = (v) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', useGrouping: 'always' }).format(Number(v) || 0);
const nInv = (n) => `${n} ${n === 1 ? 'factura' : 'facturas'}`;
const iso = (d) => d.toISOString().slice(0, 10);
const METHODS = ['Transferencia', 'PayPal', 'Bizum', 'Tarjeta', 'Efectivo', 'Otro'];
const EMPTY = { date_from: '', date_to: '', customer: 'all', payment_status: 'all', payment_method: 'all', search: '' };

const presetRange = (p) => {
  const now = new Date();
  const y = now.getFullYear();
  if (p === 'month') return [iso(new Date(Date.UTC(y, now.getMonth(), 1))), iso(now)];
  if (p === 'quarter') return [iso(new Date(Date.UTC(y, Math.floor(now.getMonth() / 3) * 3, 1))), iso(now)];
  if (p === 'year') return [`${y}-01-01`, iso(now)];
  if (p === 'lastyear') return [`${y - 1}-01-01`, `${y - 1}-12-31`];
  return ['', ''];
};

const StatCard = ({ label, value, sub, icon: Icon, tone, testid }) => (
  <Card className="p-4 flex items-start justify-between" data-testid={testid}>
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${tone}`} data-testid={`${testid}-value`}>{value}</p>
      {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
    </div>
    <Icon className={`h-5 w-5 ${tone}`} />
  </Card>
);

const ProfitFilters = ({ f, set, onReset }) => (
  <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-5">
    <Select value="" onValueChange={(p) => { const [a, b] = presetRange(p); set({ ...f, date_from: a, date_to: b }); }}>
      <SelectTrigger data-testid="profit-filter-preset"><SelectValue placeholder="Periodo rápido" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="month">Este mes</SelectItem>
        <SelectItem value="quarter">Este trimestre</SelectItem>
        <SelectItem value="year">Este año</SelectItem>
        <SelectItem value="lastyear">Año pasado</SelectItem>
        <SelectItem value="all">Todo</SelectItem>
      </SelectContent>
    </Select>
    <Input type="date" lang="es" value={f.date_from} onChange={(e) => set({ ...f, date_from: e.target.value })} data-testid="profit-filter-date-from" aria-label="Desde" />
    <Input type="date" lang="es" value={f.date_to} onChange={(e) => set({ ...f, date_to: e.target.value })} data-testid="profit-filter-date-to" aria-label="Hasta" />
    <Select value={f.payment_status} onValueChange={(v) => set({ ...f, payment_status: v })}>
      <SelectTrigger data-testid="profit-filter-status"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">Todos los estados</SelectItem>
        <SelectItem value="paid">Cobrado / Pagado</SelectItem>
        <SelectItem value="pending">Pendiente</SelectItem>
      </SelectContent>
    </Select>
    <Select value={f.payment_method} onValueChange={(v) => set({ ...f, payment_method: v })}>
      <SelectTrigger data-testid="profit-filter-method"><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">Todos los métodos</SelectItem>
        {METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
      </SelectContent>
    </Select>
    <div className="relative md:col-span-2 lg:col-span-4">
      <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
      <Input className="pl-9" placeholder="Buscar cliente, nº factura, proveedor, concepto o nº pedido..." value={f.search} onChange={(e) => set({ ...f, search: e.target.value })} data-testid="profit-filter-search" />
    </div>
    <Button variant="outline" onClick={onReset} data-testid="profit-filter-reset"><RotateCcw className="h-4 w-4 mr-2" />Limpiar</Button>
  </div>
);

const CustomerTable = ({ rows, onPick }) => (
  <div className="overflow-x-auto border rounded-lg">
    <table className="w-full text-sm" data-testid="profit-by-customer-table">
      <thead className="bg-slate-50 text-slate-600">
        <tr>
          <th className="text-left px-3 py-2">Cliente</th>
          <th className="text-right px-3 py-2">Ingresos</th>
          <th className="text-right px-3 py-2">Gastos</th>
          <th className="text-right px-3 py-2">Beneficio</th>
          <th className="text-right px-3 py-2">Margen</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={5} className="text-center text-slate-500 py-6">Sin datos para estos filtros</td></tr>}
        {rows.map((r) => (
          <tr key={r.customer_key} onClick={() => onPick(r)} title="Ver detalle del cliente" className="border-t cursor-pointer hover:bg-primary/5" data-testid={`profit-row-${r.customer_key}`}>
            <td className="px-3 py-2 font-medium text-primary hover:underline">{r.customer_name}</td>
            <td className="px-3 py-2 text-right">{eur(r.income)} <span className="text-xs text-slate-400">({r.income_count})</span></td>
            <td className="px-3 py-2 text-right">{eur(r.expenses)} <span className="text-xs text-slate-400">({r.expense_count})</span></td>
            <td className={`px-3 py-2 text-right font-semibold ${r.profit >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{eur(r.profit)}</td>
            <td className="px-3 py-2 text-right text-slate-600">{r.margin === null ? '—' : `${r.margin}%`}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const KIND = { income: { label: 'Ingreso', cls: 'text-emerald-700' }, expense: { label: 'Gasto', cls: 'text-red-600' } };

const CustomerDetail = ({ name, items, loading, onBack }) => (
  <div className="space-y-2" data-testid="profit-customer-detail">
    <div className="flex items-center gap-2">
      <Button variant="outline" size="sm" onClick={onBack} data-testid="profit-detail-back"><ChevronLeft className="h-4 w-4 mr-1" />Todos los clientes</Button>
      <span className="font-semibold text-slate-900" data-testid="profit-detail-name">{name}</span>
    </div>
    <div className="overflow-x-auto border rounded-lg">
      <table className="w-full text-sm" data-testid="profit-detail-table">
        <thead className="bg-slate-50 text-slate-600">
          <tr>
            <th className="text-left px-3 py-2">Tipo</th>
            <th className="text-left px-3 py-2">Fecha</th>
            <th className="text-left px-3 py-2">Factura</th>
            <th className="text-left px-3 py-2">Cliente / Proveedor</th>
            <th className="text-left px-3 py-2">Pedidos</th>
            <th className="text-left px-3 py-2">Estado</th>
            <th className="text-right px-3 py-2">Base</th>
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={7} className="text-center text-slate-500 py-6">{loading ? 'Cargando…' : 'Sin facturas con estos filtros'}</td></tr>}
          {items.map((it) => (
            <tr key={`${it.kind}-${it.id}`} className="border-t" data-testid={`profit-detail-${it.kind}-${it.id}`}>
              <td className={`px-3 py-2 font-medium ${KIND[it.kind].cls}`}>{KIND[it.kind].label}</td>
              <td className="px-3 py-2 text-slate-600">{it.date ? it.date.split('-').reverse().join('/') : '—'}</td>
              <td className="px-3 py-2">{it.invoice_number || '—'}</td>
              <td className="px-3 py-2 text-slate-700">{it.who || '—'}</td>
              <td className="px-3 py-2 text-xs text-slate-500 max-w-[220px] truncate" title={it.orders.map((o) => `#${o}`).join(' ')}>{it.orders.map((o) => `#${o}`).join(' ') || '—'}</td>
              <td className="px-3 py-2 text-slate-600">{it.status || '—'}</td>
              <td className={`px-3 py-2 text-right font-semibold ${KIND[it.kind].cls}`}>{it.kind === 'expense' ? '-' : ''}{eur(it.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export const ProfitSummaryPanel = ({ refreshKey }) => {
  const [f, setF] = useState(EMPTY);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pickedName, setPickedName] = useState('');

  useEffect(() => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v && v !== 'all'));
    setLoading(true);
    const t = setTimeout(async () => {
      try { setData((await axios.get(`${API}/admin/accounting/profit-summary`, { params })).data); } catch { toast.error('No se pudo cargar el resumen de beneficios'); } finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [f, refreshKey]);

  const tt = data?.totals;
  return (
    <Card className="p-4 space-y-4 border-primary/20" data-testid="profit-summary-panel">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-bold text-slate-900">Resumen de beneficios</p>
          <p className="text-xs text-slate-500">Ingresos y gastos sin IVA (base imponible). Los gastos se asignan al cliente del pedido vinculado.</p>
        </div>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
      </div>
      <ProfitFilters f={f} set={setF} onReset={() => { setF(EMPTY); setPickedName(''); }} />
      {tt && (
        <div className="grid gap-3 md:grid-cols-3">
          <StatCard label="Ingresos" value={eur(tt.income)} sub={nInv(tt.income_count)} icon={ArrowUpRight} tone="text-emerald-700" testid="profit-total-income" />
          <StatCard label="Gastos" value={eur(tt.expenses)} sub={`${nInv(tt.expense_count)}${tt.unassigned_expenses ? ` · ${eur(tt.unassigned_expenses)} sin cliente` : ''}`} icon={ArrowDownRight} tone="text-red-600" testid="profit-total-expenses" />
          <StatCard label="Beneficio neto" value={eur(tt.profit)} sub={tt.margin === null ? '—' : `Margen ${tt.margin}%`} icon={Scale} tone={tt.profit >= 0 ? 'text-primary' : 'text-red-600'} testid="profit-total-profit" />
        </div>
      )}
      {data && (f.customer !== 'all'
        ? <CustomerDetail name={pickedName} items={data.items || []} loading={loading || !data.items} onBack={() => { setF({ ...f, customer: 'all' }); setPickedName(''); }} />
        : <CustomerTable rows={data.by_customer} onPick={(r) => { setPickedName(r.customer_name); setF({ ...f, customer: r.customer_key }); }} />)}
    </Card>
  );
};
